export const MIN_FEE_ADJUSTMENT_REASON_LENGTH = 10;

export const normalizeModuleCode = (code) => String(code || '').trim().toUpperCase();

export const getCatalogModuleFee = (module) => Math.max(0, Number(module?.moduleFee) || 0);

export const isModuleFeeAdjusted = (catalogFee, adjustedFee) => {
  const catalog = Math.max(0, Number(catalogFee) || 0);
  const fee = Math.max(0, Number(adjustedFee) || 0);
  return Math.round(catalog * 100) !== Math.round(fee * 100);
};

export const getModulePricingOptions = (module, currency = 'LKR') => {
  const catalogFee = getCatalogModuleFee(module);
  const options = [];

  // 1. Standard Module Fee
  options.push({
    id: 'standard',
    label: 'Standard Module Fee',
    shortLabel: 'Module Fee',
    fee: catalogFee,
    feeType: 'MODULE_FEE',
    isRepeat: false,
    repeatAttempt: null,
    displayText: `${currency} ${catalogFee.toLocaleString()}`
  });

  // 2. Configured Repeat Attempts (e.g. Attempt 1, 2, 3...)
  const attempts = Array.isArray(module?.moduleRepeatAttempts) ? module.moduleRepeatAttempts : [];
  if (attempts.length > 0) {
    attempts.forEach((att) => {
      const attemptNum = Number(att?.attemptNumber);
      const fee = Math.max(0, Number(att?.fee) || 0);
      options.push({
        id: `repeat_attempt_${attemptNum}`,
        label: `Repeat Fee (Attempt ${attemptNum})`,
        shortLabel: `Repeat (Att. ${attemptNum})`,
        fee,
        feeType: 'REPEAT_FEE',
        isRepeat: true,
        repeatAttempt: attemptNum,
        displayText: `${currency} ${fee.toLocaleString()}`
      });
    });
  } else if (Number(module?.moduleRepeatFee) > 0) {
    const fee = Math.max(0, Number(module.moduleRepeatFee) || 0);
    options.push({
      id: 'repeat_flat',
      label: 'Repeat Fee',
      shortLabel: 'Repeat Fee',
      fee,
      feeType: 'REPEAT_FEE',
      isRepeat: true,
      repeatAttempt: 1,
      displayText: `${currency} ${fee.toLocaleString()}`
    });
  }

  return options;
};

export const isRepeatFeeModule = (module, fallback = null) => {
  if (!module && !fallback) return false;
  const target = module || {};
  const alt = fallback || {};
  if (target.isRepeat === true || target.feeType === 'REPEAT_FEE') return true;
  if (alt.isRepeat === true || alt.feeType === 'REPEAT_FEE') return true;
  if (target.repeatAttempt != null && target.repeatAttempt !== '' && !isNaN(Number(target.repeatAttempt))) return true;
  if (alt.repeatAttempt != null && alt.repeatAttempt !== '' && !isNaN(Number(alt.repeatAttempt))) return true;
  const reason = String(target.feeAdjustmentReason || alt.feeAdjustmentReason || '');
  if (/repeat/i.test(reason)) return true;
  const repeatFee = Number(target.repeatFee ?? alt.repeatFee ?? target.moduleRepeatFee ?? alt.moduleRepeatFee);
  const catalogFee = Number(target.catalogModuleFee ?? alt.catalogModuleFee ?? target.moduleFee ?? alt.moduleFee);
  const currentFee = Number(target.moduleFee ?? alt.moduleFee);
  if (repeatFee > 0 && Math.round(currentFee * 100) === Math.round(repeatFee * 100) && Math.round(currentFee * 100) !== Math.round(catalogFee * 100)) {
    return true;
  }
  return false;
};

export const getRepeatAttemptNumber = (module, fallback = null) => {
  const target = module || {};
  const alt = fallback || {};
  if (target.repeatAttempt != null && target.repeatAttempt !== '' && !isNaN(Number(target.repeatAttempt))) {
    return Number(target.repeatAttempt);
  }
  if (alt.repeatAttempt != null && alt.repeatAttempt !== '' && !isNaN(Number(alt.repeatAttempt))) {
    return Number(alt.repeatAttempt);
  }
  const reason = String(target.feeAdjustmentReason || alt.feeAdjustmentReason || '');
  const match = reason.match(/repeat\s*attempt\s*#?(\d+)/i);
  if (match) {
    return Number(match[1]);
  }
  return null;
};

export const createEmptyFeeOverride = (module) => ({
  moduleFee: getCatalogModuleFee(module),
  feeAdjustmentReason: '',
  feeType: 'MODULE_FEE',
  isRepeat: false,
  repeatAttempt: null,
  catalogModuleFee: getCatalogModuleFee(module)
});

export const buildFeeOverrideFromSelection = (selection, catalogModule) => {
  const catalogFee = selection?.catalogModuleFee != null
    ? Number(selection.catalogModuleFee)
    : getCatalogModuleFee(catalogModule || selection);

  const isRepeat = isRepeatFeeModule(selection, catalogModule);
  const repeatAttempt = isRepeat ? getRepeatAttemptNumber(selection, catalogModule) : null;
  const feeType = isRepeat ? 'REPEAT_FEE' : (selection?.feeType || 'MODULE_FEE');

  return {
    moduleFee: selection?.moduleFee != null ? Number(selection.moduleFee) : catalogFee,
    feeAdjustmentReason: selection?.feeAdjustmentReason || '',
    feeType,
    isRepeat,
    repeatAttempt,
    catalogModuleFee: catalogFee
  };
};

export const validateFeeOverride = (catalogFee, override = {}) => {
  const isRepeat = isRepeatFeeModule(override);
  if (isRepeat) {
    return { valid: true };
  }

  const catalog = Math.max(0, Number(catalogFee) || 0);
  const fee = Math.max(0, Number(override.moduleFee) || 0);
  const reason = String(override.feeAdjustmentReason || '').trim();

  if (!isModuleFeeAdjusted(catalog, fee)) {
    return { valid: true };
  }

  if (reason.length < MIN_FEE_ADJUSTMENT_REASON_LENGTH) {
    return {
      valid: false,
      message: `Reason is required (minimum ${MIN_FEE_ADJUSTMENT_REASON_LENGTH} characters) when fee differs from catalog`
    };
  }

  return { valid: true };
};

export const buildModuleSelectionPayload = (module, override = {}) => {
  const catalogFee = getCatalogModuleFee(module);
  const fee = override?.moduleFee != null
    ? Number(override.moduleFee)
    : (override?.flatFee != null ? Number(override.flatFee) : catalogFee);
  const isRepeat = isRepeatFeeModule(override, module);
  const repeatAttempt = isRepeat ? getRepeatAttemptNumber(override, module) : null;
  const feeType = isRepeat ? 'REPEAT_FEE' : 'MODULE_FEE';
  const adjusted = isRepeat || isModuleFeeAdjusted(catalogFee, fee);
  const reason = isRepeat
    ? (repeatAttempt ? `Repeat Attempt #${repeatAttempt}` : 'Repeat Fee')
    : String(override?.feeAdjustmentReason || '').trim();

  return {
    stageName: module.stageName,
    stageNumber: module.stageNumber,
    code: module.code,
    name: module.name,
    credits: module.credits,
    moduleFee: fee,
    feeType,
    isRepeat,
    repeatAttempt,
    moduleRepeatFee: Number(module?.moduleRepeatFee) || 0,
    moduleRepeatAttempts: Array.isArray(module?.moduleRepeatAttempts) ? module.moduleRepeatAttempts : [],
    ...(adjusted
      ? {
        catalogModuleFee: catalogFee,
        feeAdjusted: true,
        feeAdjustmentReason: reason
      }
      : {}),
    examination: module.examination
  };
};

export const validateModuleFeeOverrides = (modules, overridesByCode = {}) => {
  for (const module of modules) {
    const code = normalizeModuleCode(module.code);
    const override = overridesByCode[code] || createEmptyFeeOverride(module);
    const result = validateFeeOverride(getCatalogModuleFee(module), override);
    if (!result.valid) {
      return { valid: false, message: `${module.code}: ${result.message}` };
    }
  }
  return { valid: true };
};

export const formatAdjustedFeeLabel = (module, currency = 'LKR') => {
  const fee = Number(module?.moduleFee) || 0;
  const isRepeat = isRepeatFeeModule(module);

  if (isRepeat) {
    const attempt = getRepeatAttemptNumber(module);
    const attemptText = attempt ? ` (Attempt ${attempt})` : '';
    return `Repeat Fee${attemptText}: ${currency} ${fee.toLocaleString()}`;
  }

  if (!module?.feeAdjusted) {
    return fee > 0 ? `${currency} ${fee.toLocaleString()}` : null;
  }

  const catalog = Number(module.catalogModuleFee) || 0;
  return `${currency} ${fee.toLocaleString()} (catalog ${currency} ${catalog.toLocaleString()})`;
};
